import { useEffect, useRef } from "react";
import type { ConnectorWithOAuth } from "../../../shared/connectors/getTargetValue";
import type { ConnectorOAuthConfig } from "../../../shared/connectors/types";
import { ExpectedError } from "../../../shared/expectedError";
import { connectorOauthStartPath } from "../../lib/apiPaths";
import { apiUrl, connectorOauthRedirectUri } from "../../lib/tenantOrigin";

const managedBase = "https://credentials.tines.com/webhook";

const width = 1000;
const height = 700;

const listenerInterval = 50;

function centerParams(): string {
  const left = Math.round((window.screen.width - width) / 2);
  const top = Math.round((window.screen.height - height) / 2);
  return `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`;
}

function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .toLowerCase();
}

// Connectors whose credentials story lives at a dedicated webhook path rather
// than the shared oauth-redirect/oauth-return dispatcher.
const customCredentialsStories: Record<
  string,
  { begin: string; return: string }
> = {
  slackUser: {
    begin: `${managedBase}/slack-user-oauth/go`,
    return: `${managedBase}/slack-userbot-return/go`,
  },
};

const managedOAuthUrl = (provider: string) => {
  const custom = customCredentialsStories[provider];
  if (custom) return `${custom.begin}?is_cloud=true`;
  if (provider.includes("google")) {
    return `${managedBase}/google-begin/go?type=${provider.toLowerCase()}&is_cloud=true`;
  }
  return `${managedBase}/oauth-redirect/go?type=${toKebabCase(provider)}&is_cloud=true`;
};

const managedOauthRedirectUri = (provider: string) => {
  const custom = customCredentialsStories[provider];
  if (custom) return custom.return;
  if (provider.includes("google")) {
    return `${managedBase}/google-return/go`;
  }
  return `${managedBase}/oauth-return/go`;
};

export function useOauthPopupWindow() {
  const externalApiUrl = apiUrl();
  const popup = useRef<Window | null>(null);
  const interval = useRef<number | null>(null);
  const listener = useRef<((event: MessageEvent) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (listener.current) removeEventListener("message", listener.current);
      if (interval.current) clearInterval(interval.current);
      if (popup.current && !popup.current.closed) popup.current.close();
    };
  }, []);

  const run = async <T>(
    url: string,
    parseMessage: (event: MessageEvent) => T | null,
    expectedOrigin: string
  ): Promise<T | null> => {
    popup.current = window.open(url, "oauth", centerParams());
    if (!popup.current) return null;

    return new Promise((resolve) => {
      const parse = (event: MessageEvent) => {
        if (event.origin !== expectedOrigin) return;
        // Any script can postMessage to our window, so a message the parser
        // rejects must not escape this listener and wedge the popup.
        let parsed: T | null;
        try {
          parsed = parseMessage(event);
        } catch {
          return;
        }
        if (parsed) {
          removeEventListener("message", parse);
          listener.current = null;
          popup.current?.close();
          if (interval.current) {
            clearInterval(interval.current);
            interval.current = null;
          }
          resolve(parsed);
        }
      };

      listener.current = parse;
      addEventListener("message", parse);

      interval.current = window.setInterval(() => {
        if (popup.current?.closed) {
          removeEventListener("message", parse);
          listener.current = null;
          if (interval.current) {
            clearInterval(interval.current);
            interval.current = null;
          }
          resolve(null);
        }
      }, listenerInterval);
    });
  };

  async function auth(
    oauthConfig: ConnectorOAuthConfig,
    app: ConnectorWithOAuth,
    identifier: string
  ) {
    const { clientCredentialsFrom } = oauthConfig;
    if (clientCredentialsFrom === "env") {
      const managedOrigin = new URL(managedBase).origin;
      const result = await run(
        managedOAuthUrl(identifier),
        (event) => {
          if (
            typeof event.data === "object" &&
            event.data?.type === "tines_connect_request"
          ) {
            popup.current?.postMessage(
              { type: "tines_connect_response" },
              event.origin
            );
            return null;
          }
          const parsed = JSON.parse(JSON.parse(event.data)[0].value);
          const code = parsed.managedOauthCode as string;
          if (!code) return null;
          const configuration = parsed.metadata as
            | Record<string, string>
            | undefined;
          return { code, configuration };
        },
        managedOrigin
      );
      if (!result) return null;
      const redirectUri = managedOauthRedirectUri(identifier);
      return {
        code: result.code,
        redirectUri,
        configuration: result.configuration,
      };
    } else {
      const startUrl = new URL(
        `${externalApiUrl}${connectorOauthStartPath(identifier)}`
      );
      startUrl.searchParams.set("app_id", app.id);
      // The success page is served at the tenant callback URL the server
      // minted (getRedirectURI), so listen on that origin, not the app's.
      const callbackOrigin = new URL(connectorOauthRedirectUri(identifier))
        .origin;
      const result = await flow(startUrl.toString(), callbackOrigin);
      if (!result) return null;
      if ("error" in result) throw new ExpectedError(result.error);
      return { managed: true as const };
    }
  }

  // Runs a popup whose terminal page posts closePopupWindow's {status, message}.
  async function flow(
    url: string,
    expectedOrigin: string
  ): Promise<
    { success: true; installationId?: string } | { error: string } | null
  > {
    return run(
      url,
      (event) => {
        const data = (
          typeof event.data === "string" ? JSON.parse(event.data) : event.data
        ) as {
          status?: string;
          message?: string;
          installationId?: string;
        } | null;
        if (data?.status === "error")
          return { error: data.message ?? "Setup failed" };
        if (data?.status === "success")
          return {
            success: true as const,
            installationId: data.installationId,
          };
        return null;
      },
      expectedOrigin
    );
  }

  return { auth, flow };
}
